import { Fragment, type ReactNode } from 'react';
import { ProgressiveSection } from '../ui/ProgressiveSection';

// Captura guiada para toda la app (FI-22, Entrega 1): las piezas visuales que nacieron en Capturar
// ticket, como componentes sin lógica de negocio. Regla de forma: hasta seis opciones excluyentes se
// muestran como tarjetas con círculo; etiquetas cortas hasta ~12, como chips; más, desplegable con
// buscador. Nada llega preseleccionado: `value` vacío es un estado normal, no un error.
// Las clases `wiz-*` ya están en producción (globals.css); aquí solo se encapsulan.

/** La pregunta: lo único en negrita de la pantalla. */
export function Question({ children }: { children: ReactNode }) {
  return <h3 className="wiz-question">{children}</h3>;
}

/** Sangría con línea guía: lo que cuelga de una pregunta (respuestas, campos). */
export function Answers({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`wiz-answers${className ? ` ${className}` : ''}`}>{children}</div>;
}

export interface ChoiceOption<T extends string> {
  value: T;
  label: ReactNode;
  hint?: ReactNode;
  /** Alternativa (p. ej. «a granel»): más baja y en gris, tras un separador. */
  alt?: boolean;
}

export function ChoiceCards<T extends string>({ options, value, onChange, stack = false, variant = 'cards', ariaLabel, altSeparator }: {
  options: Array<ChoiceOption<T>>;
  value: T | '';
  onChange: (value: T) => void;
  /** Una tarjeta por fila (nombres largos). */
  stack?: boolean;
  variant?: 'cards' | 'chips';
  ariaLabel?: string;
  /** Texto del separador antes de las opciones `alt` («o si no venía empacado»). */
  altSeparator?: string;
}) {
  if (variant === 'chips') {
    return (
      <div aria-label={ariaLabel} className="wiz-answers" role="group">
        <div className="wiz-chips">
          {options.map((option) => (
            <button aria-pressed={value === option.value} className={`wiz-chip${value === option.value ? ' wiz-chip--active' : ''}`} key={option.value} onClick={() => onChange(option.value)} type="button">{option.label}</button>
          ))}
        </div>
      </div>
    );
  }
  const firstAlt = options.findIndex((option) => option.alt);
  return (
    <div aria-label={ariaLabel} className={`wiz-choices wiz-answers${stack ? ' wiz-choices--stack' : ''}`} role="group">
      {options.map((option, index) => (
        <Fragment key={option.value}>
          {altSeparator && index === firstAlt && index > 0 && <div className="wiz-or">{altSeparator}</div>}
          <button aria-pressed={value === option.value} className={`wiz-choice${value === option.value ? ' wiz-choice--active' : ''}${option.alt ? ' wiz-choice--alt' : ''}`} onClick={() => onChange(option.value)} type="button">
            <strong>{option.label}</strong>
            {option.hint && <small>{option.hint}</small>}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Bloque ya contestado: una línea con su resumen y «Cambiar». Sin `onChange` es solo lectura (p. ej.
 * un formulario ya enviado). `tone`: 'missing' = obligatorio sin contestar (ámbar); 'empty' =
 * opcional sin respuesta (gris). P-079: es la fila de resumen de `ProgressiveSection` (estado completo,
 * pendiente o vacío), para que todas las capturas la vean y la lean igual; «Cambiar» salta a otro paso,
 * sin acordeón.
 */
export function DoneRow({ label, value, onChange, tone = 'done' }: { label: string; value: ReactNode; onChange?: () => void; tone?: 'done' | 'missing' | 'empty' }) {
  return <ProgressiveSection complete={tone === 'done'} label={label} onOpen={onChange} summary={value} tone={tone} />;
}

/** Varias opciones a la vez (p. ej. «¿Qué áreas quedaron limpias?»): chips que se marcan con ✓. */
export function MultiChips({ options, values, onChange, ariaLabel }: {
  options: Array<{ value: string; label: ReactNode }>;
  values: string[];
  onChange: (values: string[]) => void;
  ariaLabel?: string;
}) {
  const toggle = (value: string) => onChange(values.includes(value) ? values.filter((current) => current !== value) : [...values, value]);
  return (
    <div aria-label={ariaLabel} className="wiz-answers" role="group">
      <div className="wiz-chips">
        {options.map((option) => (
          <button aria-pressed={values.includes(option.value)} className={`wiz-chip wiz-chip--multi${values.includes(option.value) ? ' wiz-chip--active' : ''}`} key={option.value} onClick={() => toggle(option.value)} type="button">{option.label}</button>
        ))}
      </div>
    </div>
  );
}

/** «Agregar algo nuevo»: un enlace al final del grupo, nunca una tarjeta. */
export function AddLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return <button className="link-button wiz-add" onClick={onClick} type="button">{children}</button>;
}

/** Motivo por el que no se puede avanzar: en gris, sin fondo, en el pie del modal. P-076: dentro del pie del
 *  diálogo del rediseño ocupa su propio renglón (`n3-dialog__note`), no una columna como los botones. */
export function FooterReason({ children }: { children: ReactNode }) {
  return children ? <div className="wiz-reason n3-dialog__note">{children}</div> : null;
}
